import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ComponentRef,
  Inject,
  OnDestroy,
  Optional,
  Type,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ITableCustomColumn } from '../../models';
import {
  ITableCustomColumnCredentials,
  ITableCustomColumnCredentialsItem,
  UI_TABLE_CUSTOM_COLUMN,
} from '../../tokens';
import { UITableBaseColumn } from '../table-base-column/table-base-column.component';

@Component({
  selector: 'ui-table-body-custom-column',
  templateUrl: './table-body-custom-column.component.html',
  styleUrls: ['./table-body-custom-column.component.scss'],
  standalone: true,
})
export class UITableBodyCustomColumn extends UITableBaseColumn implements AfterViewInit, OnDestroy {
  @ViewChild('componentRef', { read: ViewContainerRef }) container: ViewContainerRef | undefined;

  private _ref: ComponentRef<UITableBaseColumn> | undefined;

  private destroy$: Subject<void> = new Subject();

  constructor(
    private cdr: ChangeDetectorRef,
    @Optional()
    @Inject(UI_TABLE_CUSTOM_COLUMN)
    private tableColumnComponents: ITableCustomColumnCredentials,
  ) {
    super();
  }

  ngAfterViewInit(): void {
    const component = (this.config as ITableCustomColumn)?.component;

    if (!component) {
      return;
    }

    if (typeof component !== 'string') {
      this.createComponentFromReference(component);
    } else {
      this.createComponentFromKey(component);
    }
  }

  private createComponentFromReference(componentRef: Promise<Type<UITableBaseColumn>>): void {
    componentRef
      .then(componentType => {
        this.createComponent(componentType);
      })
      .catch(error => {
        console.error(`Failed to load component with key "${componentRef}":`, error);
      });
  }

  private createComponentFromKey(key: string): void {
    const comp = this.getComponentByKey(key);
    if (!comp?.component) {
      console.warn(`Component with key "${key}" not found`);
      return;
    }

    comp
      .component()
      .then(componentType => {
        this.createComponent(componentType);
      })
      .catch(error => {
        console.error(`Failed to load component with key "${key}":`, error);
      });
  }

  private createComponent(componentType: Type<UITableBaseColumn>): void {
    if (!this.container) {
      console.warn('Container not available for component creation');
      return;
    }

    this._ref = this.container.createComponent(componentType);

    if (!this._ref) {
      console.warn('Failed to create component instance');
      return;
    }

    this.setupComponentInstance();
    this.cdr.detectChanges();
  }

  private setupComponentInstance(): void {
    if (!this._ref) {
      return;
    }

    this._ref.instance.config = this.config;
    this._ref.instance.data = this.data;

    if (this._ref.instance.updateTableData) {
      this._ref.instance.updateTableData
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => this.onUpdateTableData());
    }
  }

  getComponentByKey(key: string): ITableCustomColumnCredentialsItem {
    return this.tableColumnComponents[key];
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this._ref?.destroy();
  }
}
